import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerComponent.css';

// Grid size used by Material's year-picker view (4 cols x 4 rows layout).
const YEAR_RANGE_SIZE = 16;

// Mirrors react-datepicker's internal getYearsPeriod so header text and grid
// always show the same 16-year window.
const computeYearRange = (year) => {
    const end = Math.ceil(year / YEAR_RANGE_SIZE) * YEAR_RANGE_SIZE;
    return { start: end - (YEAR_RANGE_SIZE - 1), end };
};

const VIEW_DAY = 'day';
const VIEW_MONTH = 'month';
const VIEW_YEAR = 'year';

const handleNavClick = (handler) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    handler?.();
};

const DatePickerComponent = (props) => {
    const {
        dateField,
        handleDateChange,
        fieldName,
        dateFormat,
        timeSelect = false,
        isRange = false,
        showFutureDate = true,
        adjustDateOnChange = false,
        onChange,
        selected,
        className,
        maxDate,
        onKeyDown,
        showIcon,
        selectsRange,
        renderCustomHeader,
        popperClassName,
        shouldCloseOnSelect,
        ...restProps
    } = props;

    const isRangePicker = isRange || !!selectsRange;
    const parentSelected = useMemo(() => {
        if (typeof selected !== 'undefined') return selected;
        if (isRangePicker) return undefined;
        return dateField ? new Date(dateField) : null;
    }, [selected, dateField, isRangePicker]);

    // Track the year/month the user is browsing so a partial pick in the
    // year or month grid carries over to the next view without committing.
    const [pickerView, setPickerView] = useState(VIEW_DAY);
    const [viewedDate, setViewedDate] = useState(parentSelected instanceof Date ? parentSelected : null);

    useEffect(() => {
        if (parentSelected instanceof Date) {
            setViewedDate(parentSelected);
        }
    }, [parentSelected]);

    const goToYearView = useCallback(() => {
        setPickerView((prev) => (prev === VIEW_YEAR ? VIEW_DAY : VIEW_YEAR));
    }, []);
    const goToDayView = useCallback(() => setPickerView(VIEW_DAY), []);

    const isYearView = pickerView === VIEW_YEAR;
    const isMonthView = pickerView === VIEW_MONTH;
    const isNonDayView = isYearView || isMonthView;

    const effectiveSelected = !isRangePicker && isNonDayView
        ? (viewedDate || parentSelected || new Date())
        : parentSelected;

    const handleChange = useCallback((date, event) => {
        if (isYearView) {
            if (date instanceof Date) setViewedDate(date);
            setPickerView(VIEW_MONTH);
            return;
        }
        if (isMonthView) {
            if (date instanceof Date) setViewedDate(date);
            setPickerView(VIEW_DAY);
            return;
        }
        if (date instanceof Date) {
            setViewedDate(date);
        }
        const handler = onChange || ((d) => handleDateChange?.(d, fieldName));
        handler(date, event);
    }, [isYearView, isMonthView, onChange, handleDateChange, fieldName]);

    const dayViewHeader = useCallback((h) => (
        <div className="wmsDatePickerHeaderNav" aria-label="Date navigation">
            <button
                type="button"
                className="wmsDatePickerHeaderLabelBtn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleNavClick(goToYearView)}
                aria-label="Switch to year selection"
            >
                <span className="wmsDatePickerHeaderLabel">{moment(h.date).format('MMM YYYY')}</span>
                <span className="wmsDatePickerHeaderChevron" aria-hidden="true">&#9662;</span>
            </button>
            <div className="wmsDatePickerHeaderArrows">
                <button
                    type="button"
                    className="wmsDatePickerHeaderNavBtn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleNavClick(h.decreaseYear)}
                    disabled={h.prevYearButtonDisabled}
                    aria-label="Previous year"
                >
                    &#171;
                </button>
                <button
                    type="button"
                    className="wmsDatePickerHeaderNavBtn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleNavClick(h.decreaseMonth)}
                    disabled={h.prevMonthButtonDisabled}
                    aria-label="Previous month"
                >
                    &#8249;
                </button>
                <button
                    type="button"
                    className="wmsDatePickerHeaderNavBtn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleNavClick(h.increaseMonth)}
                    disabled={h.nextMonthButtonDisabled}
                    aria-label="Next month"
                >
                    &#8250;
                </button>
                <button
                    type="button"
                    className="wmsDatePickerHeaderNavBtn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleNavClick(h.increaseYear)}
                    disabled={h.nextYearButtonDisabled}
                    aria-label="Next year"
                >
                    &#187;
                </button>
            </div>
        </div>
    ), [goToYearView]);

    const yearViewHeader = useCallback((h) => {
        const currentYear = moment(h.date).year();
        const { start, end } = computeYearRange(currentYear);
        const jumpYears = (delta) => {
            const nextYear = currentYear + delta;
            // Move the highlighted year with the anchor so single-year steps are visible.
            setViewedDate((prev) => {
                const base = prev instanceof Date ? new Date(prev) : new Date();
                base.setFullYear(nextYear);
                return base;
            });
            if (typeof h.changeYear === 'function') {
                h.changeYear(nextYear);
                return;
            }
            const step = delta > 0 ? h.increaseYear : h.decreaseYear;
            const count = Math.abs(delta);
            for (let i = 0; i < count; i += 1) step?.();
        };
        return (
            <div className="wmsDatePickerHeaderNav" aria-label="Year range navigation">
                <button
                    type="button"
                    className="wmsDatePickerHeaderLabelBtn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleNavClick(goToDayView)}
                    aria-label="Switch back to date view"
                >
                    <span className="wmsDatePickerHeaderLabel">{start} &ndash; {end}</span>
                    <span className="wmsDatePickerHeaderChevron" aria-hidden="true">&#9652;</span>
                </button>
                <div className="wmsDatePickerHeaderArrows">
                    <button
                        type="button"
                        className="wmsDatePickerHeaderNavBtn"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleNavClick(() => jumpYears(-1))}
                        disabled={h.prevYearButtonDisabled}
                        aria-label="Previous year"
                    >
                        &#171;
                    </button>
                    <button
                        type="button"
                        className="wmsDatePickerHeaderNavBtn"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleNavClick(() => jumpYears(1))}
                        disabled={h.nextYearButtonDisabled}
                        aria-label="Next year"
                    >
                        &#187;
                    </button>
                </div>
            </div>
        );
    }, [goToDayView]);

    const monthViewHeader = useCallback((h) => {
        const currentYear = moment(h.date).year();
        const jumpYears = (delta) => {
            if (typeof h.changeYear === 'function') {
                h.changeYear(currentYear + delta);
                return;
            }
            const step = delta > 0 ? h.increaseYear : h.decreaseYear;
            const count = Math.abs(delta);
            for (let i = 0; i < count; i += 1) step?.();
        };
        return (
            <div className="wmsDatePickerHeaderNav" aria-label="Month selection navigation">
                <button
                    type="button"
                    className="wmsDatePickerHeaderLabelBtn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleNavClick(goToYearView)}
                    aria-label="Switch to year selection"
                >
                    <span className="wmsDatePickerHeaderLabel">{currentYear}</span>
                    <span className="wmsDatePickerHeaderChevron" aria-hidden="true">&#9662;</span>
                </button>
                <div className="wmsDatePickerHeaderArrows">
                    <button
                        type="button"
                        className="wmsDatePickerHeaderNavBtn"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleNavClick(() => jumpYears(-1))}
                        disabled={h.prevYearButtonDisabled}
                        aria-label="Previous year"
                    >
                        &#8249;
                    </button>
                    <button
                        type="button"
                        className="wmsDatePickerHeaderNavBtn"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleNavClick(() => jumpYears(1))}
                        disabled={h.nextYearButtonDisabled}
                        aria-label="Next year"
                    >
                        &#8250;
                    </button>
                </div>
            </div>
        );
    }, [goToYearView]);

    let activeHeader = dayViewHeader;
    if (isYearView) activeHeader = yearViewHeader;
    else if (isMonthView) activeHeader = monthViewHeader;

    let viewModifier = '';
    if (isYearView) viewModifier = 'wmsDatePickerPopper--yearView';
    else if (isMonthView) viewModifier = 'wmsDatePickerPopper--monthView';

    const mergedPopperClassName = [
        'wmsDatePickerPopper',
        viewModifier,
        popperClassName,
    ].filter(Boolean).join(' ');

    // Year/month grid views can't coexist with time / range modes, so suspend
    // those while the user is browsing the year or month grid.
    const timeSelectActive = timeSelect && !isNonDayView;
    const rangeActive = isRangePicker && !isNonDayView;
    const explicitCloseOnSelect = typeof shouldCloseOnSelect === 'boolean' ? shouldCloseOnSelect : undefined;
    const closeOnSelect = isNonDayView ? false : explicitCloseOnSelect;

    return (
        <DatePicker
            {...restProps}
            className={className || 'date-picker form-control'}
            showIcon={typeof showIcon === 'boolean' ? showIcon : true}
            autoComplete='one-time-code'
            dateFormat={dateFormat || 'MM-dd-yyyy'}
            showTimeSelect={timeSelectActive}
            selected={effectiveSelected}
            onChange={handleChange}
            name={props.name || 'dateField'}
            adjustDateOnChange={adjustDateOnChange}
            selectsRange={rangeActive}
            maxDate={!showFutureDate ? new Date() : maxDate}
            fixedHeight
            popperClassName={mergedPopperClassName}
            showYearPicker={isYearView}
            yearItemNumber={YEAR_RANGE_SIZE}
            showMonthYearPicker={isMonthView}
            shouldCloseOnSelect={closeOnSelect}
            onCalendarClose={() => {
                setPickerView(VIEW_DAY);
                setViewedDate(parentSelected instanceof Date ? parentSelected : null);
            }}
            renderCustomHeader={renderCustomHeader || activeHeader}
            onKeyDown={(e) => {
                e?.preventDefault?.();
                if (typeof onKeyDown === 'function') {
                    onKeyDown(e);
                }
            }}
        />
    );
};

DatePickerComponent.propTypes = {
    dateField: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date)
    ]),
    handleDateChange: PropTypes.func,
    fieldName: PropTypes.string,
    dateFormat: PropTypes.string,
    timeSelect: PropTypes.bool,
    isRange: PropTypes.bool,
    showFutureDate: PropTypes.bool,
    adjustDateOnChange: PropTypes.bool,
    onChange: PropTypes.func,
    selected: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date)
    ]),
    className: PropTypes.string,
    maxDate: PropTypes.instanceOf(Date),
    onKeyDown: PropTypes.func,
    showIcon: PropTypes.bool,
    selectsRange: PropTypes.bool,
    renderCustomHeader: PropTypes.func,
    popperClassName: PropTypes.string,
    shouldCloseOnSelect: PropTypes.bool,
    name: PropTypes.string
};

export default DatePickerComponent;